import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { getTheme } from './themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children, userId = null }) => {
  const [themeMode, setThemeMode] = useState(() => {
    // If no user is logged in, always use dark theme as default
    if (!userId) {
      return 'dark';
    }
    
    // Get user-specific theme from localStorage
    const savedTheme = localStorage.getItem(`tienda-theme-${userId}`);
    return savedTheme || 'dark'; // Default to dark if no preference saved
  });

  const theme = getTheme(themeMode);

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    
    // Only save to localStorage if user is logged in
    if (userId) {
      localStorage.setItem(`tienda-theme-${userId}`, newMode);
    }
  };

  const setTheme = (mode) => {
    setThemeMode(mode);
    
    // Only save to localStorage if user is logged in
    if (userId) {
      localStorage.setItem(`tienda-theme-${userId}`, mode);
    }
  };

  // Effect to load user theme when userId changes (user logs in/out)
  useEffect(() => {
    if (!userId) {
      // User logged out, set to default dark theme
      setThemeMode('dark');
    } else {
      // User logged in, load their saved theme preference
      const savedTheme = localStorage.getItem(`tienda-theme-${userId}`);
      setThemeMode(savedTheme || 'dark');
    }
  }, [userId]);

  const value = {
    themeMode,
    toggleTheme,
    setTheme,
    isDark: themeMode === 'dark',
    isLight: themeMode === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
